import { HiddenNote } from "@/components/HiddenNote";
import { ClientShortcutSlot } from "@/components/admin/ClientShortcutSlot";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Radar, LogOut, Search, GitCompareArrows, Home, Users, RefreshCw, Sparkles, Building2, ListPlus, Link as LinkIcon, Calculator, ChevronDown, Menu, X, Shield, Layers, Bell, Loader2, AlertCircle, EyeOff, Crosshair, Clock, Target, ShieldAlert, Radio } from "lucide-react";
import { api, fmtNumber } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EmailWatermark from "@/components/EmailWatermark";
import ConsentModal from "@/components/ConsentModal";
import { startTracker, trackPageView } from "@/lib/tracker";
import { useUnaffiliatedEnabled, useXCreatorsEnabled, LISTS_ENABLED } from "@/lib/featureFlags";
import { useBotDetectionAccess } from "@/lib/botDetection";
import { useLiveIntelAccess, liveSubtitle } from "@/lib/liveIntel";
import { useCreator360Access } from "@/lib/creator360";
import { Gem } from "lucide-react";
import { GemDrawerHost } from "@/components/c360/GemMark";
import { LiveDrawerHost } from "@/components/live/LiveDrawer";
import { DemoBlurNav, DemoBlurStyle } from "@/components/DemoBlurControl";


// v65 — third Creator Intel item; only rendered for people with access.
const LiveIntelMenuItem = ({ onPick }) => {
  const { enabled, features } = useLiveIntelAccess();
  if (!enabled) return null;
  return (
    <NavLink
      to="/live-streaming-intel"
      data-testid="nav-live-intel"
      onClick={onPick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-t hairline ${
          isActive ? "bg-rose-500/10 text-rose-200" : "text-zinc-300 hover:text-rose-200 hover:bg-white/5"
        }`
      }
    >
      <span className="w-1.5 h-1.5 rounded-full bg-rose-400 ml-0.5 animate-pulse" />
      <div className="flex items-center justify-between w-full gap-3">
        <div>
          <div className="font-medium">Live Streaming Intel</div>
          <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap" data-testid="nav-live-intel-sub">{liveSubtitle(features)}</div>
        </div>
        <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 shrink-0" data-testid="nav-live-intel-new">New</span>
      </div>
    </NavLink>
  );
};

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium tracking-tight rounded-md transition-colors duration-150 whitespace-nowrap ${
        isActive
          ? "bg-white/5 text-white"
          : "text-zinc-400 hover:text-white hover:bg-white/5"
      }`
    }
  >
    <Icon size={16} strokeWidth={1.6} /> {label}
  </NavLink>
);

const CreatorIntelNav = () => {
  // Round-9 Item 4: hover-and-tap submenu with two choices under
  // Creator Intel — "Affiliated creators" (/search, exactly like
  // today) and "Unaffiliated creators" (/search?tab=unaffiliated).
  // Hover opens on desktop; the base button is itself a <NavLink> to
  // /search so a direct click (keyboard, mobile tap) opens the
  // affiliated view immediately. Chevron tap on mobile opens the
  // submenu without navigating. Mirrors the AlertsNav pattern.
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const unaffEnabled = useUnaffiliatedEnabled();
  const xEnabled = useXCreatorsEnabled();
  // v42a — the Creator Intel menu button must also show the current
  // section when the user is on /my-radar (it's part of Creator
  // Intel). Otherwise the menu reverts to the inactive style while
  // the user is clearly on a Creator Intel page.
  // v43 — same for /x-creators (preview-only new segment).
  const onSearch = location.pathname.startsWith("/search") || location.pathname.startsWith("/x-creators") || location.pathname.startsWith("/live-streaming-intel");
  const openMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); setOpen(true); };
  const closeMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setOpen(false), 120); };
  // Fall back to the plain NavItem when the Unaffiliated flag is off —
  // nothing to pick from in that case.
  if (!unaffEnabled) {
    // v42a — even with the Unaffiliated flag off, My Radar is still
    // on. Fall back to a two-item dropdown instead of the plain nav
    // link so the My Radar entry stays reachable from the menu.
    return (
      <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
        <div className="flex items-center">
          <NavLink
            to="/search"
            data-testid="nav-search"
            className={() =>
              `flex items-center gap-1.5 pl-2.5 pr-1 py-1.5 text-[13px] font-medium tracking-tight rounded-l-md transition-colors duration-150 whitespace-nowrap ${
                onSearch ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`
            }
            onClick={() => setOpen(false)}
          >
            <Search size={16} strokeWidth={1.6} /> Creator Intel
          </NavLink>
          <button
            type="button"
            data-testid="nav-search-chevron"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open Creator Intel submenu"
            className={`pr-2 pl-1 py-1.5 rounded-r-md transition-colors duration-150 ${
              onSearch ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <ChevronDown size={12} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
        {open && (
          <div
            data-testid="nav-search-menu"
            className="absolute top-full left-0 mt-1 min-w-[260px] rounded-md border hairline overflow-hidden shadow-2xl z-50"
            style={{ background: "var(--surface-2)" }}
          >
            <NavLink
              to="/search"
              data-testid="nav-search-affiliated"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-b hairline ${
                  isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Users size={14} strokeWidth={1.6} style={{ color: "var(--primary)" }} />
              <div>
                <div className="font-medium">Affiliated creators</div>
                <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">The creator grid, exchanges, markets</div>
              </div>
            </NavLink>
            <LiveIntelMenuItem onPick={() => setOpen(false)} />
                Unaffiliated is off, keeping the Creator Intel order:
                Affiliated → (Unaffiliated) → X Creators → My Radar → Workspace. */}
            {xEnabled && (
              <NavLink
                to="/x-creators"
                data-testid="nav-x-creators"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-t hairline ${
                    isActive ? "bg-sky-500/10 text-sky-200" : "text-zinc-300 hover:text-sky-200 hover:bg-white/5"
                  }`
                }
              >
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 ml-0.5" />
                <div className="flex items-center justify-between w-full gap-3">
                  <div>
                    <div className="font-medium">X Creators</div>
                    <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Indian traders on X</div>
                  </div>
                  <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-200 shrink-0" data-testid="nav-x-creators-new">New</span>
                </div>
              </NavLink>
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <div className="flex items-center">
        <NavLink
          to="/search"
          data-testid="nav-search"
          className={() =>
            `flex items-center gap-1.5 pl-2.5 pr-1 py-1.5 text-[13px] font-medium tracking-tight rounded-l-md transition-colors duration-150 whitespace-nowrap ${
              onSearch ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`
          }
          onClick={() => setOpen(false)}
        >
          <Search size={16} strokeWidth={1.6} /> Creator Intel
        </NavLink>
        <button
          type="button"
          data-testid="nav-search-chevron"
          onClick={() => setOpen((v) => !v)}
          aria-label="Open Creator Intel submenu"
          className={`pr-2 pl-1 py-1.5 rounded-r-md transition-colors duration-150 ${
            onSearch ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
          }`}
        >
          <ChevronDown size={12} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div
          data-testid="nav-search-menu"
          className="absolute top-full left-0 mt-1 min-w-[260px] rounded-md border hairline overflow-hidden shadow-2xl z-50"
          style={{ background: "var(--surface-2)" }}
        >
          <NavLink
            to="/search"
            data-testid="nav-search-affiliated"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-b hairline ${
                isActive && !location.search.includes("tab=unaffiliated") ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Users size={14} strokeWidth={1.6} style={{ color: "var(--primary)" }} />
            <div>
              <div className="font-medium">Affiliated creators</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">The creator grid, exchanges, markets</div>
            </div>
          </NavLink>
          <NavLink
            to="/search?tab=unaffiliated"
            data-testid="nav-search-unaffiliated"
            onClick={() => setOpen(false)}
            className={() =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                onSearch && location.search.includes("tab=unaffiliated") ? "bg-amber-500/10 text-amber-200" : "text-zinc-300 hover:text-amber-200 hover:bg-white/5"
              }`
            }
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 ml-0.5" />
            <div>
              <div className="font-medium">Unaffiliated creators</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Trader audiences no exchange has yet</div>
            </div>
          </NavLink>
          <LiveIntelMenuItem onPick={() => setOpen(false)} />
          {/* v44 item 2 — X Creators (preview only) sits between
              Unaffiliated and My Radar so Creator Intel reads
              Affiliated → Unaffiliated → X Creators → My Radar → Workspace. */}
          {xEnabled && (
            <NavLink
              to="/x-creators"
              data-testid="nav-x-creators"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-t hairline ${
                  isActive ? "bg-sky-500/10 text-sky-200" : "text-zinc-300 hover:text-sky-200 hover:bg-white/5"
                }`
              }
            >
              <span className="w-1.5 h-1.5 rounded-full bg-sky-400 ml-0.5" />
              <div className="flex items-center justify-between w-full gap-3">
                <div>
                  <div className="font-medium">X Creators</div>
                  <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Indian traders on X</div>
                </div>
                <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-200 shrink-0" data-testid="nav-x-creators-new">New</span>
              </div>
            </NavLink>
          )}
        </div>
      )}
    </div>
  );
};

// v58 — My Radar gets its own top menu (moved out of Creator Intel).
const MY_RADAR_ITEMS = [
  { to: "/my-radar", testid: "nav-my-radar", label: "My Radar", sub: "Your private creator pipeline", dot: "var(--primary)" },
  { to: "/my-radar/workspace", testid: "nav-my-radar-workspace", label: "My Workspace", sub: "A free-form page just for you", dot: "var(--accent)" },
];

const MyRadarNav = () => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const isActive = location.pathname.startsWith("/my-radar");
  const openMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); setOpen(true); };
  const closeMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setOpen(false), 120); };
  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <button
        data-testid="nav-my-radar-menu-btn"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium tracking-tight rounded-md transition-colors duration-150 whitespace-nowrap ${
          isActive ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <Target size={16} strokeWidth={1.6} /> My Radar
        <ChevronDown size={12} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="nav-my-radar-menu" className="absolute top-full left-0 mt-1 min-w-[260px] rounded-md border hairline overflow-hidden shadow-2xl z-50" style={{ background: "var(--surface-2)" }}>
          {MY_RADAR_ITEMS.map((it, i) => (
            <NavLink key={it.to} to={it.to} end data-testid={it.testid} onClick={() => setOpen(false)}
              className={({ isActive: a }) => `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${i ? "" : "border-b hairline"} ${
                a ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"}`}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: it.dot }} />
              <div>
                <div className="font-medium">{it.label}</div>
                <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">{it.sub}</div>
              </div>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const AlertsNav = () => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const isActive = location.pathname === "/alerts";
  const openMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); setOpen(true); };
  const closeMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setOpen(false), 120); };
  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <button
        data-testid="nav-alerts"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium tracking-tight rounded-md transition-colors duration-150 whitespace-nowrap ${
          isActive ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <Bell size={16} strokeWidth={1.6} /> Alerts
        <ChevronDown size={12} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="nav-alerts-menu"
          className="absolute top-full left-0 mt-1 min-w-[260px] rounded-md border hairline overflow-hidden shadow-2xl z-50"
          style={{ background: "var(--surface-2)" }}
        >
          <NavLink
            to="/alerts"
            data-testid="nav-alerts-creator"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-b hairline ${
                isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Users size={14} strokeWidth={1.6} style={{ color: "var(--primary)" }} />
            <div>
              <div className="font-medium">Creator Alerts</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Watch specific creators or lists</div>
            </div>
          </NavLink>
          <NavLink
            to="/alerts?view=radar"
            data-testid="nav-alerts-radar"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Radar size={14} strokeWidth={1.6} style={{ color: "var(--accent)" }} />
            <div>
              <div className="font-medium">Radar Alerts</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Watch the entire market</div>
            </div>
          </NavLink>
        </div>
      )}
    </div>
  );
};


const ExchangeIntelNav = () => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const isActive = location.pathname === "/exchange-mapping" || location.pathname === "/gap-matrix" || location.pathname === "/prospect-radar";
  const openMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); setOpen(true); };
  const closeMenu = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setOpen(false), 120); };
  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <button
        data-testid="nav-mapping"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium tracking-tight rounded-md transition-colors duration-150 whitespace-nowrap ${
          isActive ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <Building2 size={16} strokeWidth={1.6} /> Exchange Intel
        <ChevronDown size={12} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="nav-mapping-menu"
          className="absolute top-full left-0 mt-1 min-w-[300px] rounded-md border hairline overflow-hidden shadow-2xl z-50"
          style={{ background: "var(--surface-2)" }}
        >
          <NavLink
            to="/exchange-mapping"
            data-testid="nav-exchange-mapping"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-b hairline ${
                isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Building2 size={14} strokeWidth={1.6} style={{ color: "var(--primary)" }} />
            <div>
              <div className="font-medium">Exchange Mapping</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Market-share breakdown by exchange</div>
            </div>
          </NavLink>
          <NavLink
            to="/gap-matrix"
            data-testid="nav-gap-matrix"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-b hairline ${
                isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Layers size={14} strokeWidth={1.6} style={{ color: "var(--accent)" }} />
            <div>
              <div className="font-medium">Gap &amp; Exclusion Matrix</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Bi-directional creator gaps vs competitors</div>
            </div>
          </NavLink>
          <NavLink
            to="/prospect-radar"
            data-testid="nav-prospect-radar"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Crosshair size={14} strokeWidth={1.6} style={{ color: "var(--primary)" }} />
            <div>
              <div className="font-medium">Prospect Radar</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Creators who fit but haven&apos;t promoted you yet</div>
            </div>
          </NavLink>
        </div>
      )}
    </div>
  );
};


const CompareNav = () => {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const isActive = location.pathname === "/compare" || location.pathname === "/exchange-compare";

  const openMenu = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(true);
  };
  const closeMenu = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <button
        data-testid="nav-compare"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium tracking-tight rounded-md transition-colors duration-150 whitespace-nowrap ${
          isActive ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <GitCompareArrows size={16} strokeWidth={1.6} /> Compare
        <ChevronDown size={12} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="nav-compare-menu"
          className="absolute top-full left-0 mt-1 min-w-[280px] rounded-md border hairline overflow-hidden shadow-2xl z-50"
          style={{ background: "var(--surface-2)" }}
        >
          <NavLink
            to="/compare"
            data-testid="nav-compare-creators"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 border-b hairline ${
                isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Users size={14} strokeWidth={1.6} style={{ color: "var(--primary)" }} />
            <div>
              <div className="font-medium">Compare Creators</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Benchmark KOLs side-by-side</div>
            </div>
          </NavLink>
          <NavLink
            to="/exchange-compare"
            data-testid="nav-compare-exchanges"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                isActive ? "bg-white/5 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Building2 size={14} strokeWidth={1.6} style={{ color: "var(--accent)" }} />
            <div>
              <div className="font-medium">Compare Exchanges</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap">Benchmark competitor platforms</div>
            </div>
          </NavLink>
        </div>
      )}
    </div>
  );
};

function AiAssistant({ onDone }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const submit = async () => {
    if (!q.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/parse", { query: q });
      // Route intent overrides pure filter application when the user asks about
      // any other module (Exchange Intel, ROI, Lists, Compare, mapping, ...).
      if (data.nav_route && typeof data.nav_route === "string") {
        toast.success(`Opening ${data.nav_route}`);
        navigate(data.nav_route);
        onDone?.(data);
        return;
      }
      // Stash so Search.js can consume it whether it's mounted or about to mount
      try { sessionStorage.setItem("kolradar:ai-filter", JSON.stringify(data)); } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent("kolradar:ai-filter", { detail: data }));
      const bits = [];
      if (data.exchanges?.length) bits.push(`Exchanges: ${data.exchanges.join(", ")}`);
      if (data.exact_match) bits.push("Exclusive");
      Object.entries(data.filters || {}).forEach(([k, v]) => bits.push(`${k.replace("min_", "≥")}=${v}`));
      if (data.telegram) bits.push(`TG=${data.telegram}`);
      if (data.onboard_days) bits.push(`≤${data.onboard_days}d`);
      if (data.bias?.length) bits.push(`Bias: ${data.bias.join("+")}`);
      if (data.upload_bias?.length) bits.push(`Upload: ${data.upload_bias.join("+")}`);
      if (data.tiers?.length) bits.push(`Tier: ${data.tiers.join("+")}`);
      toast.success(bits.length ? `Applied · ${bits.join(" · ")}` : "Applied (no filters parsed)");
      onDone?.(data);
    } catch {
      toast.error("Assistant failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">AI Query Assistant</div>
      <textarea
        data-testid="ai-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => (e.key === "Enter" && !e.shiftKey) && (e.preventDefault(), submit())}
        placeholder='Ask anything — "Open Delta Exchange", "Model an ROI deal", "Compare Binance vs CoinDCX", or "Tier 1 forex livestreamers with 500k+ subs"'
        rows={3}
        className="w-full px-3 py-2 text-sm rounded-md border hairline bg-transparent outline-none focus:border-white/30 placeholder:text-zinc-600 resize-none"
      />
      <div className="flex justify-between items-center mt-3">
        <span className="mono text-[10px] text-zinc-500">Enter to apply</span>
        <button
          data-testid="ai-submit-btn"
          onClick={submit}
          disabled={!q.trim() || busy}
          className="text-xs px-3 py-1.5 rounded-md font-medium disabled:opacity-40"
          style={{ background: "var(--primary)", color: "#0A0A0B" }}
        >{busy ? "Parsing…" : "Apply"}</button>
      </div>
      <div className="mt-3 text-[10px] text-zinc-500 leading-relaxed">
        Try: "Show me the Exness dashboard" · "Open ROI calculator" · "Tier 1 crypto creators with 1M+ subs"
      </div>
    </div>
  );
}

function ManualOnboard({ onDone }) {
  const [url, setUrl] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [busy, setBusy] = useState(false);
  // Persistent inline error surfaced when Apps Script reports the channel
  // didn't match any tracked exchange (or any other failure). Complements
  // the toast — toasts auto-dismiss in ~4s and a BD who looked away can
  // miss the reason nothing got added.
  const [noMatchMessage, setNoMatchMessage] = useState(null);
  const [hiddenNote, setHiddenNote] = useState(null);
  const [notice, setNotice] = useState(null);
  // lastOnboarded: null → {id, name, kind, list, status: 'adding' | 'ready'}
  const [lastOnboarded, setLastOnboarded] = useState(null);
  const nav = useNavigate();
  const watchRef = useRef(0);
  useEffect(() => () => { watchRef.current += 1; }, []);

  // v57 — is he on KOL Radar yet? Checks both kinds; never shows "not found".
  const findOnRadar = async (id, kind) => {
    const order = kind === "unaffiliated" ? ["unaffiliated", "kol"] : ["kol", "unaffiliated"];
    for (const k of order) {
      try {
        if (k === "kol") { const { data } = await api.get(`/kols/${id}`); if (data?.id) return { kind: "kol", name: data.channel_name }; }
        else { const { data } = await api.get(`/unaffiliated/creator/${id}`); if (data?.creator) return { kind: "unaffiliated", name: data.creator.channel_name }; }
      } catch { /* not there (yet) */ }
    }
    return null;
  };
  // Show him at once if he is there; otherwise "Being added" and keep checking.
  const watchUntilReady = async (v, name) => {
    const base = { id: v.id, name: name || v.id, kind: v.kind, list: v.list };
    const started = Date.now();
    const mine = ++watchRef.current;
    let first = true;
    while (Date.now() - started < 900_000 && mine === watchRef.current) {
      const hit = await findOnRadar(v.id, v.kind);
      if (mine !== watchRef.current) return;
      if (hit) {
        setLastOnboarded((prev) => (prev && prev.id !== v.id ? prev : { ...base, kind: hit.kind, name: hit.name || base.name, status: "ready" }));
        onDone?.();
        return;
      }
      if (first) { setLastOnboarded({ ...base, status: "adding" }); first = false; }
      await new Promise((r) => setTimeout(r, 10_000));
    }
  };

  // v50 — the server answers with a job; poll it (two rounds of doors can
  // take a few minutes, so there is no early give-up here).
  const waitForJob = async (first) => {
    let data = first;
    const started = Date.now();
    while (data && data.status === "checking" && data.job_id && Date.now() - started < 600_000) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        data = (await api.get(`/onboard/manual/jobs/${data.job_id}`)).data;
      } catch { /* network blip — keep waiting */ }
    }
    return data && data.status === "done" ? data : null;
  };

  const submit = async () => {
    if (!url.trim() || busy) return;
    setBusy(true);
    watchRef.current += 1;
    setNoMatchMessage(null);
    setHiddenNote(null);
    setNotice(null);
    try {
      const body = { channel_url: url };
      if (contactEmail.trim()) body.contact_email = contactEmail.trim();
      if (contactPhone.trim()) body.contact_phone = contactPhone.trim();
      const { data: started } = await api.post("/onboard/manual", body);
      const res = await waitForJob(started);
      const line = res?.message || "We couldn't check this creator right now. Please try again in a little while.";
      const outcome = res?.outcome || "try_later";
      if (outcome === "added") {
        toast.success(line);
        const v = res.view;
        if (v && v.id) {
          setLastOnboarded({ id: v.id, name: res.channel_name || v.id, status: "adding", kind: v.kind, list: v.list });
          watchUntilReady(v, res.channel_name);
        } else {
          setLastOnboarded(null);
          onDone?.();
        }
      } else if (outcome === "already" && res.view?.id) {
        toast.message(line);
        watchUntilReady(res.view, res.channel_name);
      } else if (outcome === "received") {
        toast.message(line);
        setNotice(line);
        setLastOnboarded(null);
      } else {
        toast.error(line);
        setNoMatchMessage({ text: line, outcome });
        setLastOnboarded(null);
      }
      setUrl("");
      setContactEmail("");
      setContactPhone("");
      setShowContact(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Duplicate responses come back with the existing creator's id so we
      // can deep-link straight to that profile (unaffiliated or promoter).
      if (detail && typeof detail === "object" && detail.hidden && detail.hidden_id) {
        // v69 — admins: a calm note with Unhide / View in Hidden Creators, no red error.
        setHiddenNote({ id: detail.hidden_id, name: detail.hidden_name || detail.hidden_id });
      } else if (detail && typeof detail === "object" && detail.existing_id) {
        const kind = detail.existing_kind === "unaffiliated" ? "unaffiliated" : "kol";
        // v59 — nothing is wrong here: a calm, plain note (same action as before).
        const who = detail.existing_name || "This creator";
        toast(`${who} is already on KOL Radar${detail.existing_list ? ` · ${detail.existing_list}` : ""}.`, {
          action: {
            label: "View existing creator",
            onClick: () => nav(kind === "unaffiliated" ? `/unaffiliated/${detail.existing_id}` : `/kol/${detail.existing_id}`),
          },
          duration: 8000,
        });
        setLastOnboarded({ id: detail.existing_id, name: detail.existing_name || detail.existing_id, status: "ready", kind, list: detail.existing_list });
      } else {
        const st = err?.response?.status;
        const plain = (st === 429 && typeof detail === "string") ? detail
          : (detail && typeof detail === "object" && detail.message) ? detail.message
          : st === 400 ? "Please use the channel's YouTube link, like youtube.com/@name."
          : "We couldn't check this creator right now. Please try again in a little while.";
        toast.error(plain);
        setNoMatchMessage({ text: plain, outcome: "error" });
      }
    } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Manual Onboard</div>
      <div className="text-[11px] text-zinc-500 leading-relaxed mb-3" data-testid="onboard-hint">
        Add creators who actively promote the crypto exchanges tracked here. Duplicates, inactive creators, and anyone with no recent tracked promotions are rejected automatically.
      </div>
      <input data-testid="onboard-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
             onKeyDown={(e) => e.key === "Enter" && submit()}
             placeholder="https://www.youtube.com/@creator"
             className="w-full px-3 py-2 text-sm rounded-md border hairline bg-transparent outline-none focus:border-white/30 placeholder:text-zinc-600" />

      {showContact ? (
        <div className="mt-2 space-y-2" data-testid="onboard-contact-fields">
          <input
            data-testid="onboard-email-input"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="contact email (optional)"
            className="w-full px-3 py-2 text-sm rounded-md border hairline bg-transparent outline-none focus:border-white/30 placeholder:text-zinc-600"
          />
          <input
            data-testid="onboard-phone-input"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="contact phone (optional, e.g. +91 98765 43210)"
            className="w-full px-3 py-2 text-sm rounded-md border hairline bg-transparent outline-none focus:border-white/30 placeholder:text-zinc-600"
          />
          <div className="text-[10px] text-zinc-600 leading-relaxed">
            Only fill these if you already have them handy. Otherwise the scan writes them if the channel lists an email/phone publicly.
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="onboard-contact-toggle"
          onClick={() => setShowContact(true)}
          className="mt-2 text-[11px] text-zinc-400 hover:text-cyan-400"
        >+ Add contact details (optional)</button>
      )}

      <div className="flex justify-end items-center mt-3 gap-2">
        {busy && (
          <span data-testid="onboard-inflight" className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" style={{ color: "var(--primary)" }} />
            Checking… this can take a minute or two
          </span>
        )}
        <button data-testid="onboard-submit-btn" onClick={submit} disabled={!url.trim() || busy}
                className="text-xs px-3 py-1.5 rounded-md font-medium disabled:opacity-40 inline-flex items-center gap-1.5"
                style={{ background: "var(--primary)", color: "#0A0A0B" }}>
          {busy && <Loader2 size={12} className="animate-spin" />}
          {busy ? "Sending…" : "Submit"}
        </button>
      </div>
      {notice && !busy && (
        <div data-testid="onboard-received" className="mt-3 border hairline rounded-md px-3 py-2 text-[13px] text-white flex items-start gap-2" style={{ background: "rgba(0,240,255,0.06)", borderColor: "rgba(0,240,255,0.3)" }}>
          <AlertCircle size={14} className="shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
          <div className="flex-1 min-w-0">{notice}</div>
          <button data-testid="onboard-received-dismiss" onClick={() => setNotice(null)} className="text-zinc-500 hover:text-white shrink-0 p-0.5" aria-label="Dismiss"><X size={12} /></button>
        </div>
      )}
      {hiddenNote && !busy && <div className="mt-2"><HiddenNote id={hiddenNote.id} name={hiddenNote.name} testid="onboard-hidden-note" /></div>}
      {noMatchMessage && !busy && (
        <div
          data-testid="onboard-no-match"
          className="mt-3 border rounded-md px-3 py-2 flex items-start gap-2 text-xs"
          style={{ background: "rgba(244,63,94,0.08)", borderColor: "rgba(244,63,94,0.35)" }}
        >
          <AlertCircle size={14} className="shrink-0 mt-0.5" style={{ color: "#F43F5E" }} />
          <div className="flex-1 min-w-0">
            <div className="text-white text-[13px] leading-snug" data-testid="onboard-no-match-text">{noMatchMessage.text}</div>
            {noMatchMessage.outcome !== "try_later" && noMatchMessage.outcome !== "error" && (
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                Nothing was added · try a different URL or check for typos
              </div>
            )}
          </div>
          <button
            data-testid="onboard-no-match-dismiss"
            onClick={() => setNoMatchMessage(null)}
            className="text-zinc-500 hover:text-white shrink-0 p-0.5"
            aria-label="Dismiss"
          ><X size={12} /></button>
        </div>
      )}
      {lastOnboarded && (() => {
        const isReady = lastOnboarded.status === "ready";
        const where = lastOnboarded.list ? `On KOL Radar · ${lastOnboarded.list}` : "On KOL Radar";
        return (
          <div
            data-testid="onboard-open-profile-prompt"
            data-status={isReady ? "ready" : "adding"}
            className="mt-4 border hairline rounded-md p-3 flex items-center justify-between gap-3"
            style={{
              background: isReady ? "rgba(57,255,20,0.08)" : "rgba(0,240,255,0.06)",
              borderColor: isReady ? "rgba(57,255,20,0.35)" : "rgba(0,240,255,0.3)",
            }}
          >
            <div className="min-w-0 flex items-center gap-2">
              {!isReady && <RefreshCw size={13} className="animate-spin shrink-0" style={{ color: "var(--primary)" }} />}
              <div className="min-w-0">
                <div className="text-sm text-white truncate" title={lastOnboarded.name} data-testid="onboard-open-profile-name"><span data-blur-id={lastOnboarded.id}>{lastOnboarded.name}</span></div>
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5" data-testid="onboard-open-profile-list">
                  {isReady ? where : `Being added — ready in a few minutes${lastOnboarded.list ? ` · ${lastOnboarded.list}` : ""}`}
                </div>
              </div>
            </div>
            <Link
              data-testid="onboard-open-profile-btn"
              to={!isReady ? "#" : (lastOnboarded.kind === "unaffiliated" ? `/unaffiliated/${lastOnboarded.id}` : `/kol/${lastOnboarded.id}`)}
              onClick={(e) => {
                if (!isReady) { e.preventDefault(); return; }
                setLastOnboarded(null);
              }}
              aria-disabled={!isReady}
              className="text-xs px-3 py-1.5 rounded-md font-medium shrink-0 inline-block"
              style={{ background: "var(--primary)", color: "#0A0A0B", opacity: isReady ? 1 : 0.4, pointerEvents: isReady ? "auto" : "none" }}
            >
              {isReady ? "View →" : "Being added…"}
            </Link>
          </div>
        );
      })()}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout, reconnecting } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [syncing, setSyncing] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const unaffEnabled = useUnaffiliatedEnabled();
  const xEnabled = useXCreatorsEnabled();
  const botDetection = useBotDetectionAccess().enabled;
  const liveIntel = useLiveIntelAccess().enabled;
  const creator360 = useCreator360Access().enabled;
  const [freshness, setFreshness] = useState(null);
  const [freshnessDismissed, setFreshnessDismissed] = useState(false);

  // Boot the usage tracker exactly once, only for authenticated users.
  // No-op on landing / login. All the heavy lifting (visibility, activity,
  // batching, sendBeacon) lives in tracker.js — the shell just kicks it off.
  // Skip when `?notrack=1` is on the URL — used only for perf A/B measurement.
  useEffect(() => {
    if (!user) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("notrack") === "1") return;
    } catch { /* ignore */ }
    startTracker();
  }, [user]);

  // Fire a page_view on every route change. Cheap — it de-dupes internally
  // and only enqueues; the flusher batches sends.
  // v40c: pass `?tab=` so the tracker can split Creator Intel into its
  // affiliated / unaffiliated sub-sections.
  useEffect(() => {
    if (user) trackPageView(location.pathname + (location.search || ""));
  }, [location.pathname, location.search, user]);

  // Freshness banner poll — 60s, only when authenticated and only when
  // the tab is visible (so a background tab never keeps polling).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await api.get("/analytics/freshness");
        if (!cancelled) setFreshness(data);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  const handleRefresh = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/refresh");
      toast.success(`Synced · ${fmtNumber(data.kol_count)} KOLs · ${fmtNumber(data.video_count)} videos`);
      window.dispatchEvent(new Event("kolradar:refresh"));
    } catch {
      toast.error("Refresh failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {user && <ConsentModal />}
      {/* v41d — tiny banner that shows up only when /auth/me has been
          failing with a non-401 (5xx / timeout / network) for more than
          ~4 seconds. The user stays signed in; AuthContext retries
          every 3s on its own. The banner disappears the moment /auth/me
          answers again. */}
      {reconnecting && (
        <div
          data-testid="auth-reconnecting-banner"
          className="w-full text-xs text-center py-1.5 text-zinc-200"
          style={{ background: "rgba(234,179,8,0.15)", borderBottom: "1px solid rgba(234,179,8,0.35)" }}
        >
          Reconnecting to the server… you're still signed in.
        </div>
      )}
      <header className="sticky top-0 z-40 border-b hairline backdrop-blur-xl" style={{ background: "rgba(10,10,11,0.75)" }}>
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 h-14">
          <button
            data-testid="brand-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 group"
          >
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "var(--primary)" }}>
              <Radar size={16} strokeWidth={2} color="#0A0A0B" />
            </div>
            <span className="display text-[15px] font-semibold tracking-tight">
              KOL <span style={{ color: "var(--primary)" }}>Radar</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/" icon={Home} label="Home" testid="nav-home" />
            <CreatorIntelNav />
            <ExchangeIntelNav />
            {creator360 && <NavItem to="/creator-360" icon={Gem} label="Creator 360" testid="nav-creator360" />}
            <CompareNav />
            <NavItem to="/roi-calculator" icon={Calculator} label="ROI Calculator" testid="nav-roi" />
            <MyRadarNav />
            {botDetection && <NavItem to="/bot-detection" icon={ShieldAlert} label="Bot Detection" testid="nav-bot-detection" />}
            {LISTS_ENABLED && <NavItem to="/lists" icon={ListPlus} label="Lists" testid="nav-lists" />}
            <AlertsNav />
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <button
                  data-testid="mobile-menu-btn"
                  onClick={() => setMobileOpen((v) => !v)}
                  className="md:hidden text-zinc-400 hover:text-white transition-colors duration-150 p-1"
                  aria-label="Toggle menu"
                >
                  {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
                {user.is_super_admin && <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="manual-onboard-btn" className="text-zinc-400 hover:text-white transition-colors duration-150 p-1 flex items-center gap-1" title="Manually onboard a channel">
                      <LinkIcon size={13} strokeWidth={1.8} style={{ color: "var(--accent)" }} />
                      <span className="mono text-[10px] uppercase tracking-wider hidden sm:inline">Onboard</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-96 p-4 border hairline" style={{ background: "var(--surface-2)" }}>
                    <ManualOnboard onDone={handleRefresh} />
                  </PopoverContent>
                </Popover>}
                {/* Ask AI is currently disabled — button and its endpoint
                    are both switched off. Kept in the source for a
                    quick revert; delete the `false &&` guard to restore. */}
                {false && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="ai-assistant-btn"
                      className="text-zinc-400 hover:text-white transition-colors duration-150 p-1 flex items-center gap-1"
                      title="AI query assistant"
                    >
                      <Sparkles size={14} style={{ color: "var(--primary)" }} strokeWidth={1.8} />
                      <span className="mono text-[10px] uppercase tracking-wider hidden sm:inline">Ask AI</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-96 p-4 border hairline" style={{ background: "var(--surface-2)" }}>
                    <AiAssistant onDone={(data) => {
                      // Only default to Discover when the AI didn't pick a route itself.
                      if (!data || !data.nav_route) navigate("/search");
                    }} />
                  </PopoverContent>
                </Popover>
                )}
                {user.is_super_admin && (
                  <button
                    data-testid="refresh-btn"
                    onClick={handleRefresh}
                    disabled={syncing}
                    title="Resync Google Sheet (super-admin only, 1/min)"
                    className="text-zinc-400 hover:text-cyan-400 disabled:opacity-40 transition-colors duration-150 p-1"
                  >
                    <RefreshCw size={14} className={syncing ? "animate-spin" : ""} strokeWidth={1.8} />
                  </button>
                )}
                <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-400">
                  {user.picture && (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full border hairline" />
                  )}
                  <span className="mono" data-testid="user-email">{user.email}</span>
                </div>
                {user.is_super_admin && (
                  <div className="relative group" data-testid="nav-admin-group">
                    <NavLink
                      to="/admin"
                      data-testid="nav-admin"
                      className={({ isActive }) => `text-xs flex items-center gap-1 px-2 py-1 rounded-md border hairline transition-colors duration-150 ${isActive ? "text-white border-white/40" : "text-zinc-400 hover:text-white hover:border-white/30"}`}
                      title="Super-admin: manage approved users"
                    >
                      <Shield size={12} style={{ color: "var(--primary)" }} /> Admin
                    </NavLink>
                    {/* v84 — two direct links · v87 — three */}
                    <div className="absolute right-0 top-full pt-1.5 z-50 hidden group-hover:block group-focus-within:block" data-testid="nav-admin-menu">
                      <div className="min-w-[180px] rounded-md border hairline p-1 shadow-2xl" style={{ background: "var(--surface-2)" }}>
                        <Link to="/admin/feature-access" data-testid="nav-admin-feature-access" className="block px-3 py-2 rounded text-xs text-zinc-300 hover:text-white hover:bg-white/5 transition-colors duration-150">Feature access</Link>
                        <Link to="/admin/hidden-creators" data-testid="nav-admin-reserved" className="block px-3 py-2 rounded text-xs text-zinc-300 hover:text-white hover:bg-white/5 transition-colors duration-150">Reserved</Link>
                        <Link to="/admin/unmet-demand?tab=onboarding" data-testid="nav-admin-onboarding" className="block px-3 py-2 rounded text-xs text-zinc-300 hover:text-white hover:bg-white/5 transition-colors duration-150">Onboarding requests</Link>
                      </div>
                    </div>
                  </div>
                )}
                {user.is_super_admin && <DemoBlurNav />}
                {user.is_super_admin && <ClientShortcutSlot />}
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 transition-colors duration-150"
                >
                  <LogOut size={14} /> Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="text-xs px-3 py-1.5 rounded-md border hairline hover:border-white/30 transition-colors duration-150"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>

        {/* Mobile menu drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t hairline" data-testid="mobile-menu" style={{ background: "rgba(10,10,11,0.98)" }}>
            <nav className="px-4 py-3 flex flex-col gap-1">
              {[
                { to: "/", icon: Home, label: "Home", testid: "mobile-nav-home" },
                { to: "/search", icon: Search, label: "Creator Intel · Affiliated", testid: "mobile-nav-search" },
                // Round-9 Item 4: an explicit "Unaffiliated" row on
                // mobile — the hover-only submenu from desktop
                // doesn't exist here, so this is the mobile-tap
                // equivalent.
                ...(unaffEnabled ? [{ to: "/search?tab=unaffiliated", icon: Users, label: "Creator Intel · Unaffiliated", testid: "mobile-nav-search-unaffiliated" }] : []),
                ...(liveIntel ? [{ to: "/live-streaming-intel", icon: Radio, label: "Creator Intel · Live Streaming Intel", testid: "mobile-nav-live-intel", isNew: true }] : []),
                // v44 item 2 — X Creators sits here so the mobile menu
                // reads Affiliated → Unaffiliated → X Creators → My Radar → Workspace.
                ...(xEnabled ? [{ to: "/x-creators", icon: Users, label: "Creator Intel · X Creators", testid: "mobile-nav-x-creators" }] : []),
                { to: "/exchange-mapping", icon: Building2, label: "Exchange Intel", testid: "mobile-nav-mapping" },
                { to: "/gap-matrix", icon: Layers, label: "Gap & Exclusion Matrix", testid: "mobile-nav-gap-matrix" },
                { to: "/prospect-radar", icon: Crosshair, label: "Prospect Radar", testid: "mobile-nav-prospect-radar" },
                ...(creator360 ? [{ to: "/creator-360", icon: Gem, label: "Creator 360", testid: "mobile-nav-creator360" }] : []),
                { to: "/compare", icon: Users, label: "Compare Creators", testid: "mobile-nav-compare-creators" },
                { to: "/exchange-compare", icon: GitCompareArrows, label: "Compare Exchanges", testid: "mobile-nav-compare-exchanges" },
                { to: "/roi-calculator", icon: Calculator, label: "ROI Calculator", testid: "mobile-nav-roi" },
                { to: "/my-radar", icon: Target, label: "My Radar", testid: "mobile-nav-my-radar", end: true },
                { to: "/my-radar/workspace", icon: Target, label: "My Radar · My Workspace", testid: "mobile-nav-my-radar-workspace" },
                ...(botDetection ? [{ to: "/bot-detection", icon: ShieldAlert, label: "Bot Detection", testid: "mobile-nav-bot-detection" }] : []),
                ...(LISTS_ENABLED ? [{ to: "/lists", icon: ListPlus, label: "Lists", testid: "mobile-nav-lists" }] : []),
              ].map((n) => (
                <NavLink
                  key={n.testid}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors duration-150 ${
                      isActive ? "bg-white/5 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  <n.icon size={16} strokeWidth={1.6} /> {n.label}
                  {n.isNew && <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200" data-testid={`${n.testid}-new`}>New</span>}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>
      {/* Data-freshness banner — amber >24h, red >36h. Only rendered when
          the pipeline is actually stale so the shell stays quiet at happy
          path. User can dismiss for the session. */}
      {user && freshness && !freshnessDismissed && (freshness.state === "aging" || freshness.state === "stale") && (
        <div
          data-testid="freshness-banner"
          className="border-b hairline text-xs px-6 py-2 flex items-center gap-2"
          style={{
            background: freshness.state === "stale" ? "rgba(244,63,94,0.08)" : "rgba(251,191,36,0.08)",
            borderColor: freshness.state === "stale" ? "rgba(244,63,94,0.35)" : "rgba(251,191,36,0.35)",
            color: freshness.state === "stale" ? "#F43F5E" : "#FBBF24",
          }}
        >
          <Clock size={13} />
          <span>
            {freshness.state === "stale" ? "Data is stale — " : "Data is aging — "}
            last refreshed {Math.round(freshness.worst_age_hours)}h ago
            {freshness.state === "stale" ? " (over 36h)" : ""}.
            {freshness.last_error ? ` Last error: ${freshness.last_error}.` : ""}
          </span>
          <button
            data-testid="freshness-banner-dismiss"
            onClick={() => setFreshnessDismissed(true)}
            className="ml-auto text-zinc-500 hover:text-white p-0.5"
            aria-label="Dismiss"
          ><X size={12} /></button>
        </div>
      )}
      <main>{children}</main>
      <GemDrawerHost />
      <LiveDrawerHost />
      <DemoBlurStyle />
      <EmailWatermark />
    </div>
  );
}
